import React, {useState} from "react";
import Modal from "react-bootstrap/Modal";
import styles from "./EventModal.module.css";
import stylesInputs from "../../../assets/styles/inputs.module.css";
import stylesInput from "../../../assets/styles/inputs.module.css";
import TextInputField from "../../AdminComponents/form/TextInputField";
import {Alert} from "react-bootstrap";
import {useForm} from "react-hook-form";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker.css";
import {Role} from "../../../services/models/role";
import sButtons from "../../../assets/styles/buttons.module.css";
import {useCreateEvent} from "../../../hooks/useQuery";
import {SelectOption} from "../../shared/modalStyles";
import {TimeInput} from "./TimeInput";
import {datePickerConfig} from "./datepickerStyles";
import {RoleSelect} from "../../shared/RoleSelect/RoleSelect";
import {EventType} from "../../../services/models/event";
import {EventSwitch} from "./EventSwitch";
import {TagInput} from "../../shared/TagInput/TagInput";

interface EventsModalProps {
    show?: boolean;
    onHide?: () => void;
    roles: Role[];
}

interface EventFormData {
    name: string;
    link: string;
    date: Date;
    roles: SelectOption[];
    eventType: EventType;
}

export const EventModalCreate: React.FC<EventsModalProps> = ({
                                                                 show = false, onHide = () => {
    }, roles = []
                                                             }) => {
    const {
        register,
        handleSubmit,
        formState: {errors},
        watch,
        reset,
    } = useForm<EventFormData>({
        mode: "onChange",
        defaultValues: {
            name: "",
            link: "",
            date: undefined,
            roles: [],
            eventType: "event",
        },
    });

    const [errorText, setErrorText] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [timeString, setTimeString] = useState("");
    const [selectedRoles, setSelectedRoles] = useState<SelectOption[]>([]);
    const [eventType, setEventType] = useState<EventType>("event");
    const [eventTags, setEventTags] = useState<string[]>([]);
    const [datetimeAgreed, setDatetimeAgreed] = useState<boolean>(false)
    const createEvent = useCreateEvent();
    const watchAllFields = watch();

    const handleClose = () => {
        reset();
        setSelectedDate(null);
        setTimeString("");
        setSelectedRoles([]);
        setDatetimeAgreed(false); // Сбрасываем состояние чекбокса
        onHide();
        setEventType("event");
        setEventTags([]);
    };

    const isFormValid = () => {
        // Если выбрано "по согласованию", проверяем только остальные поля
        if (datetimeAgreed) {
            return (
                watchAllFields.name?.trim() !== "" &&
                watchAllFields.link?.trim() !== "" &&
                selectedRoles.length > 0
            );
        }
        
        // Иначе проверяем все поля, включая дату и время
        const isValid =
            selectedDate &&
            timeString &&
            watchAllFields.name?.trim() !== "" &&
            watchAllFields.link?.trim() !== "" &&
            selectedRoles.length > 0;
        if (!isValid) return false;

        // Проверяем, что выбранные дата и время не в прошлом
        const [hours, minutes] = timeString.split(":").map(Number);
        const selectedDateTime = new Date(selectedDate);
        selectedDateTime.setHours(hours, minutes);
        return selectedDateTime > new Date();
    };

    const onSubmit = async (formData: EventFormData) => {
        if (!isFormValid()) return;

        let dateTimeString;

        if (datetimeAgreed) {
            // Устанавливаем очень далекую дату (31 декабря 2099 года, 23:59:59)
            const farFutureDate = new Date(2099, 11, 31, 23, 59, 59);
            dateTimeString = farFutureDate.toLocaleString("sv", {timeZone: "Europe/Moscow"}).replace(" ", "T");
        } else {
            // Форматируем дату и время с учетом московской временной зоны
            const [hours, minutes] = timeString.split(":");
            const eventDate = new Date(selectedDate!);
            eventDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

            // Создаем строку в формате ISO с явным указанием московской временной зоны
            dateTimeString = eventDate.toLocaleString("sv", {timeZone: "Europe/Moscow"}).replace(" ", "T");
        }

        const eventData = {
            name: formData.name,
            link: formData.link,
            dateTime: dateTimeString,
            guestRoles: selectedRoles.map((role) => role.value),
            eventType: eventType,
            eventTags: eventTags,
        };

        createEvent.mutate(eventData);
        handleClose();
    };

    const selectOptions = roles.map((role) => ({
        value: role.id,
        label: role.name,
        fullLabel: role.name,
    }));

    const switchTypeHandler = (type: EventType) => {
        setEventType(type);
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            aria-labelledby="contained-modal-title-vcenter"
            centered
            dialogClassName={styles.eventModalDialog}
            contentClassName={styles.modalContent}
        >
            <Modal.Header closeButton className={styles.modalHeader}>
                <Modal.Title id="contained-modal-title-vcenter">Добавить событие</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.modalBody}>
                {errorText && <Alert variant="danger">{errorText}</Alert>}
                <EventSwitch value={eventType} onChangeType={switchTypeHandler}/>
                <TextInputField
                    name="name"
                    label="Введите название события"
                    type="text"
                    placeholder="Название события"
                    register={register}
                    registerOptions={{required: "Обязательное поле"}}
                    error={errors.name}
                    className={`${styles.inputField} ${stylesInputs.mainInput}`}
                />
                <TextInputField
                    name="link"
                    label="Введите ссылку на событие"
                    type="text"
                    placeholder="https://nmic.testu.online/event123"
                    register={register}
                    registerOptions={{required: "Обязательное поле"}}
                    error={errors.link}
                    className={`${styles.inputField} ${stylesInputs.mainInput}`}
                />
                <div className={`mb-3 ${stylesInputs.formGroup}`}>
                    <label className={styles.checkboxContainer}>
                        <input
                            type="checkbox"
                            checked={datetimeAgreed}
                            onChange={() => {
                                const newValue = !datetimeAgreed;
                                setDatetimeAgreed(newValue);
                                
                                // Если чекбокс был отключен, устанавливаем текущую дату и время
                                if (!newValue) {
                                    const now = new Date();
                                    // Устанавливаем текущую дату
                                    setSelectedDate(now);
                                    // Устанавливаем текущее время с округлением до ближайших 15 минут
                                    const hours = now.getHours();
                                    const minutes = Math.ceil(now.getMinutes() / 15) * 15;
                                    const adjustedMinutes = minutes === 60 ? 0 : minutes;
                                    const adjustedHours = minutes === 60 ? hours + 1 : hours;
                                    setTimeString(`${adjustedHours.toString().padStart(2, '0')}:${adjustedMinutes.toString().padStart(2, '0')}`);
                                }
                            }}
                            className={styles.checkbox}
                        />
                        <span className={styles.checkmark}></span>
                        Дата и время по согласованию
                    </label>
                </div>
                {!datetimeAgreed && (
                    <>
                        <label className={stylesInput.label}>Введите дату и время события (время московское)</label>

                        <div className={styles.datePickerWrapper}>
                            <div>
                                <DatePicker
                                    showIcon
                                    selected={selectedDate}
                                    onChange={(date: Date | null) => setSelectedDate(date)}
                                    dateFormat="dd.MM.yyyy"
                                    placeholderText="Выберите дату"
                                    className={`${styles.inputField} ${stylesInputs.mainInput}`}
                                    minDate={new Date()}
                                    ic
                                    icon={
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M8 5.75C7.59 5.75 7.25 5.41 7.25 5V2C7.25 1.59 7.59 1.25 8 1.25C8.41 1.25 8.75 1.59 8.75 2V5C8.75 5.41 8.41 5.75 8 5.75Z"
                                                fill="#AFAFAF"
                                            />
                                            <path
                                                d="M16 5.75C15.59 5.75 15.25 5.41 15.25 5V2C15.25 1.59 15.59 1.25 16 1.25C16.41 1.25 16.75 1.59 16.75 2V5C16.75 5.41 16.41 5.75 16 5.75Z"
                                                fill="#AFAFAF"
                                            />
                                            <path
                                                d="M8.5 14.5003C8.37 14.5003 8.24 14.4703 8.12 14.4203C7.99 14.3703 7.89 14.3003 7.79 14.2103C7.61 14.0203 7.5 13.7703 7.5 13.5003C7.5 13.3703 7.53 13.2403 7.58 13.1203C7.63 13.0003 7.7 12.8903 7.79 12.7903C7.89 12.7003 7.99 12.6303 8.12 12.5803C8.48 12.4303 8.93 12.5103 9.21 12.7903C9.39 12.9803 9.5 13.2403 9.5 13.5003C9.5 13.5603 9.49 13.6303 9.48 13.7003C9.47 13.7603 9.45 13.8203 9.42 13.8803C9.4 13.9403 9.37 14.0003 9.33 14.0603C9.3 14.1103 9.25 14.1603 9.21 14.2103C9.02 14.3903 8.76 14.5003 8.5 14.5003Z"
                                                fill="#AFAFAF"
                                            />
                                            <path
                                                d="M12 14.4999C11.87 14.4999 11.74 14.4699 11.62 14.4199C11.49 14.3699 11.39 14.2999 11.29 14.2099C11.11 14.0199 11 13.7699 11 13.4999C11 13.3699 11.03 13.2399 11.08 13.1199C11.13 12.9999 11.2 12.8899 11.29 12.7899C11.39 12.6999 11.49 12.6299 11.62 12.5799C11.98 12.4199 12.43 12.5099 12.71 12.7899C12.89 12.9799 13 13.2399 13 13.4999C13 13.5599 12.99 13.6299 12.98 13.6999C12.97 13.7599 12.95 13.8199 12.92 13.8799C12.9 13.9399 12.87 13.9999 12.83 14.0599C12.8 14.1099 12.75 14.1599 12.71 14.2099C12.52 14.3899 12.26 14.4999 12 14.4999Z"
                                                fill="#AFAFAF"
                                            />
                                            <path
                                                d="M15.5 14.4999C15.37 14.4999 15.24 14.4699 15.12 14.4199C14.99 14.3699 14.89 14.2999 14.79 14.2099C14.75 14.1599 14.71 14.1099 14.67 14.0599C14.63 13.9999 14.6 13.9399 14.58 13.8799C14.55 13.8199 14.53 13.7599 14.52 13.6999C14.51 13.6299 14.5 13.5599 14.5 13.4999C14.5 13.2399 14.61 12.9799 14.79 12.7899C14.89 12.6999 14.99 12.6299 15.12 12.5799C15.49 12.4199 15.93 12.5099 16.21 12.7899C16.39 12.9799 16.5 13.2399 16.5 13.4999C16.5 13.5599 16.49 13.6299 16.48 13.6999C16.47 13.7599 16.45 13.8199 16.42 13.8799C16.4 13.9399 16.37 13.9999 16.33 14.0599C16.3 14.1099 16.25 14.1599 16.21 14.2099C16.02 14.3899 15.76 14.4999 15.5 14.4999Z"
                                                fill="#AFAFAF"
                                            />
                                            <path
                                                d="M8.5 18.0002C8.37 18.0002 8.24 17.9702 8.12 17.9202C8 17.8702 7.89 17.8002 7.79 17.7102C7.61 17.5202 7.5 17.2602 7.5 17.0002C7.5 16.8702 7.53 16.7402 7.58 16.6202C7.63 16.4902 7.7 16.3802 7.79 16.2902C8.16 15.9202 8.84 15.9202 9.21 16.2902C9.39 16.4802 9.5 16.7402 9.5 17.0002C9.5 17.2602 9.39 17.5202 9.21 17.7102C9.02 17.8902 8.76 18.0002 8.5 18.0002Z"
                                                fill="#AFAFAF"
                                            />
                                            <path
                                                d="M12 18.0002C11.74 18.0002 11.48 17.8902 11.29 17.7102C11.11 17.5202 11 17.2602 11 17.0002C11 16.8702 11.03 16.7402 11.08 16.6202C11.13 16.4902 11.2 16.3802 11.29 16.2902C11.66 15.9202 12.34 15.9202 12.71 16.2902C12.8 16.3802 12.87 16.4902 12.92 16.6202C12.97 16.7402 13 16.8702 13 17.0002C13 17.2602 12.89 17.5202 12.71 17.7102C12.52 17.8902 12.26 18.0002 12 18.0002Z"
                                                fill="#AFAFAF"
                                            />
                                            <path
                                                d="M15.5 17.9999C15.24 17.9999 14.98 17.8899 14.79 17.7099C14.7 17.6199 14.63 17.5099 14.58 17.3799C14.53 17.2599 14.5 17.1299 14.5 16.9999C14.5 16.8699 14.53 16.7399 14.58 16.6199C14.63 16.4899 14.7 16.3799 14.79 16.2899C15.02 16.0599 15.37 15.9499 15.69 16.0199C15.76 16.0299 15.82 16.0499 15.88 16.0799C15.94 16.0999 16 16.1299 16.06 16.1699C16.11 16.1999 16.16 16.2499 16.21 16.2899C16.39 16.4799 16.5 16.7399 16.5 16.9999C16.5 17.2599 16.39 17.5199 16.21 17.7099C16.02 17.8899 15.76 17.9999 15.5 17.9999Z"
                                                fill="#AFAFAF"
                                            />
                                            <path
                                                d="M20.5 9.83984H3.5C3.09 9.83984 2.75 9.49984 2.75 9.08984C2.75 8.67984 3.09 8.33984 3.5 8.33984H20.5C20.91 8.33984 21.25 8.67984 21.25 9.08984C21.25 9.49984 20.91 9.83984 20.5 9.83984Z"
                                                fill="#AFAFAF"
                                            />
                                            <path
                                                d="M16 22.75H8C4.35 22.75 2.25 20.65 2.25 17V8.5C2.25 4.85 4.35 2.75 8 2.75H16C19.65 2.75 21.75 4.85 21.75 8.5V17C21.75 20.65 19.65 22.75 16 22.75ZM8 4.25C5.14 4.25 3.75 5.64 3.75 8.5V17C3.75 19.86 5.14 21.25 8 21.25H16C18.86 21.25 20.25 19.86 20.25 17V8.5C20.25 5.64 18.86 4.25 16 4.25H8Z"
                                                fill="#AFAFAF"
                                            />
                                        </svg>
                                    }
                                    // @ts-ignore
                                    locale={datePickerConfig.locale}
                                    calendarStartDay={datePickerConfig.calendarStartDay}
                                    calendarClassName="custom-datepicker"
                                    wrapperClassName="datepicker-wrapper"
                                    popperClassName="datepicker-popper"
                                    shouldCloseOnSelect={true}
                                />
                            </div>
                            <TimeInput value={timeString} onChange={setTimeString} className={styles.inputField}/>
                        </div>
                    </>
                )}

                <div className={`${stylesInput.formGroup} mb-3`}>
                    <label className={stylesInputs.label}>Роли с доступом</label>
                    <RoleSelect
                        selectedRoles={selectedRoles}
                        setSelectedRoles={setSelectedRoles}
                        options={roles.map((role) => ({
                            value: role.id,
                            label: role.name,
                            fullLabel: role.name,
                        }))}
                        className={styles.selectWrapper}
                    />
                </div>
                <TagInput tags={eventTags} setTags={setEventTags} label="Введите теги"/>
                <div className={`d-flex gap-3 ${styles.buttonsWrapper}`}>
                    <button
                        onClick={handleSubmit(onSubmit)}
                        className={sButtons.successButton}
                        disabled={!isFormValid()}
                    >
                        Сохранить
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
};
